import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useSwipeable } from "react-swipeable";
import { Flex, Button, IconButton, Box } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useMediaQuery } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";


const Carousel = ({ children, cardWidth = 350, cardMargin = 20 }) => {
  const numItems = React.Children.count(children);
  const [itemsPerPage, setItemsPerPage] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isJumping, setIsJumping] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  const containerRef = useRef(null);
  const autoplayRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const canScrollRef = useRef(false);
  const itemsPerPageRef = useRef(1);
  const numItemsRef = useRef(numItems);

  // Keep refs in sync with state/props
  numItemsRef.current = numItems;

  const totalCardWidth = useMemo(() => cardWidth + cardMargin, [cardWidth, cardMargin]);
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const visibleDots = 4;

  const canScroll = useMemo(
    () => numItems > itemsPerPage,
    [numItems, itemsPerPage]
  );
  canScrollRef.current = canScroll;
  itemsPerPageRef.current = itemsPerPage;

  // ResizeObserver with rAF debounce to avoid layout thrashing
  useEffect(() => {
    let rafId;
    const calculate = () => {
      const width = containerRef.current?.offsetWidth || window.innerWidth;
      setContainerWidth(width);
      const calculated = Math.max(Math.floor(width / totalCardWidth), 1);
      setItemsPerPage(calculated);
    };

    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(calculate);
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    calculate();

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [totalCardWidth]);

  const totalPages = numItems;

  // Reset page position when canScroll or itemsPerPage changes
  useEffect(() => {
    if (canScroll) {
      isAnimatingRef.current = false;
      setCurrentPage(itemsPerPage);
    } else {
      isAnimatingRef.current = false;
      setIsJumping(false);
      setCurrentPage(0);
    }
  }, [canScroll, itemsPerPage]);

  const clonedChildren = useMemo(() => {
    const childrenArray = React.Children.toArray(children);
    if (!canScroll) return childrenArray;
    return [
      ...childrenArray.slice(-itemsPerPage),
      ...childrenArray,
      ...childrenArray.slice(0, itemsPerPage),
    ];
  }, [children, itemsPerPage, canScroll]);

  const logicalPage = useMemo(
    () => Math.max(0, currentPage - itemsPerPage),
    [currentPage, itemsPerPage]
  );

  const dotsStartIndex = Math.max(
    0,
    Math.min(logicalPage - Math.floor(visibleDots / 2), totalPages - visibleDots)
  );
  const dotsEndIndex = Math.min(totalPages, dotsStartIndex + visibleDots);

  const handleTransitionEnd = useCallback(() => {
    isAnimatingRef.current = false;
    setIsJumping(false);

    // Only apply infinite-loop correction if the carousel is actually scrollable
    if (!canScrollRef.current) return;

    const ipp = itemsPerPageRef.current;
    const n = numItemsRef.current;

    setCurrentPage((prev) => {
      if (prev < ipp) {
        setIsJumping(true);
        return prev + n;
      }
      if (prev > n + ipp - 1) {
        setIsJumping(true);
        return prev - n;
      }
      return prev;
    });
  }, []);

  const slide = useCallback(
    (direction) => {
      if (isAnimatingRef.current || !canScrollRef.current) return;
      isAnimatingRef.current = true;
      setCurrentPage((prevPage) =>
        direction === "NEXT" ? prevPage + 1 : prevPage - 1
      );
    },
    []
  );

  // Stable autoplay that doesn't restart on animation state changes
  useEffect(() => {
    if (!canScroll) return;

    autoplayRef.current = setInterval(() => {
      slide("NEXT");
    }, 3000);

    return () => {
      clearInterval(autoplayRef.current);
    };
  }, [slide, canScroll]);

  const goToPage = useCallback(
    (page) => {
      if (isAnimatingRef.current || !canScrollRef.current) return;
      setCurrentPage(page + itemsPerPageRef.current);
    },
    []
  );

  const handlers = useSwipeable({
    onSwipedLeft: () => slide("NEXT"),
    onSwipedRight: () => slide("PREV"),
    swipeDuration: 500,
    preventScrollOnSwipe: true,
    trackMouse: false,
    trackTouch: canScroll,
    delta: 10,
  });

  const xOffset = useMemo(
    () =>
      Math.round(
        -(currentPage * totalCardWidth) +
          (isMobile
            ? (containerWidth - itemsPerPage * totalCardWidth) / 2
            : 0)
      ),
    [currentPage, totalCardWidth, isMobile, containerWidth, itemsPerPage]
  );

  return (
    <div
      {...(canScroll ? handlers : {})}
      ref={containerRef}
      style={{
        position: "relative",
        overflow: "visible",
        maxWidth: "100%",
      }}
    >
      <Flex
        justifyContent="center"
        alignItems="center"
        position="relative"
        overflow="hidden"
        mx="auto"
        maxW="100%"
      >
        <motion.div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            width: "100%",
            willChange: "transform",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
          animate={{ x: xOffset }}
          transition={
            isJumping
              ? { duration: 0 }
              : { type: "spring", stiffness: 300, damping: 30, mass: 0.5 }
          }
          onAnimationComplete={handleTransitionEnd}
        >
          {clonedChildren?.map((child, index) => (
            <div
              key={index}
              style={{
                width: `${cardWidth}px`,
                margin: `${cardMargin / 2}px`,
                flexShrink: 0,
                paddingBottom: "20px",
              }}
            >
              {child}
            </div>
          ))}
        </motion.div>
      </Flex>
      {canScroll && (
        <Flex justifyContent="center" alignItems="center" mt={4} gap={4}>
          {isMobile ? (
            <>
              <IconButton
                aria-label="Previous property"
                icon={<ChevronLeftIcon w={6} h={6} />}
                variant="unstyled"
                color="#000"
                display="flex"
                alignItems="center"
                justifyContent="center"
                onClick={() => slide("PREV")}
              />
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                  width: `${visibleDots * 30}px`,
                  justifyContent: "center",
                }}
              >
                {Array.from({ length: totalPages }, (_, index) => index)
                  .slice(dotsStartIndex, dotsEndIndex)
                  .map((dotIndex) => (
                    <Box
                      key={dotIndex}
                      onClick={() => goToPage(dotIndex)}
                      cursor="pointer"
                      style={{
                        display: "inline-block",
                        width: logicalPage === dotIndex ? "20px" : "10px",
                        height: "10px",
                        backgroundColor:
                          logicalPage === dotIndex ? "black" : "gray",
                        borderRadius: "5px",
                        margin: "0 5px",
                        transition: "width 0.3s ease-in-out",
                      }}
                    />
                  ))}
              </Box>
              <IconButton
                aria-label="Next property"
                icon={<ChevronRightIcon w={6} h={6} />}
                variant="unstyled"
                color="#000"
                display="flex"
                alignItems="center"
                justifyContent="center"
                onClick={() => slide("NEXT")}
              />
            </>
          ) : (
            Array.from({ length: totalPages }).map((_, index) => (
              <Button
                key={index}
                onClick={() => goToPage(index)}
                mx={1}
                borderRadius="50%"
                bg={logicalPage === index ? "black" : "gray.300"}
                _hover={{ bg: "gray.400" }}
                width="8px"
                height="8px"
                minW="8px"
                p={0}
              />
            ))
          )}
        </Flex>
      )}

    </div>
  );
};

export default Carousel;
